/**
 * useCompletedTrades Hook
 *
 * Groups fills into complete position cycles (trades) and enriches them
 * with duration and category information for analytics.
 */

import { useMemo } from "react";
import type { Fill } from "@/backend/hooks/use-trade-history";
import { getTokenCategory, type CompletedTrade } from "@nordex/core";

/**
 * Parse decimal string to number
 */
function parseDecimal(value: string | number): number {
  if (typeof value === "number") return value;
  return parseFloat(value) || 0;
}

/**
 * Internal type for tracking position cycles during grouping
 */
interface PositionCycle {
  market: string;
  side: "long" | "short";
  entryTime: Date | null;
  exitTime: Date | null;
  fills: Fill[];
  totalPnl: number;
  volume: number;
  fees: number;
  entryPriceSum: number;
  entryQuantity: number;
}

/**
 * Groups fills into completed trades (full position cycles)
 * A trade is complete when fill_type is "full_close", "flip", or "liquidation"
 */
function groupFillsIntoCompletedTrades(fills: Fill[]): CompletedTrade[] {
  const completedTrades: CompletedTrade[] = [];

  // Sort fills chronologically
  const sortedFills = [...fills].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );

  // Track current position cycle per market
  const currentCycles = new Map<string, PositionCycle>();

  for (const fill of sortedFills) {
    const pnl = parseDecimal(fill.realized_pnl);
    const size = parseDecimal(fill.size);
    const price = parseDecimal(fill.price);
    const fee = parseDecimal(fill.fee);
    const market = fill.market;
    const fillType = fill.fill_type;
    const tradeValue = size * price;
    const fillTime = new Date(fill.created_at);

    // Determine position side from fill side
    // buy fill = long position, sell fill = short position (for opens)
    const positionSide: "long" | "short" = fill.side === "buy" ? "long" : "short";

    // Get or initialize current cycle for this market
    let cycle = currentCycles.get(market);

    if (fillType === "open") {
      // Starting a new position
      if (!cycle) {
        cycle = {
          market,
          side: positionSide,
          entryTime: fillTime,
          exitTime: null,
          fills: [],
          totalPnl: 0,
          volume: 0,
          fees: 0,
          entryPriceSum: 0,
          entryQuantity: 0,
        };
        currentCycles.set(market, cycle);
      }

      cycle.fills.push(fill);
      cycle.volume += tradeValue;
      cycle.fees += fee;
      cycle.entryPriceSum += price * size;
      cycle.entryQuantity += size;

      if (!cycle.entryTime) {
        cycle.entryTime = fillTime;
      }
    } else if (fillType === "partial_close") {
      // Partial close - accumulate PnL
      if (cycle) {
        cycle.fills.push(fill);
        cycle.totalPnl += pnl;
        cycle.volume += tradeValue;
        cycle.fees += fee;
      }
    } else if (fillType === "full_close" || fillType === "flip" || fillType === "liquidation") {
      // Position fully closed - create completed trade
      if (cycle || fillType === "liquidation") {
        const finalCycle = cycle || {
          market,
          side: positionSide === "long" ? "short" : "long", // Flip side for close
          entryTime: fillTime,
          exitTime: null,
          fills: [],
          totalPnl: 0,
          volume: 0,
          fees: 0,
          entryPriceSum: price * size,
          entryQuantity: size,
        };

        finalCycle.fills.push(fill);
        finalCycle.totalPnl += pnl;
        finalCycle.volume += tradeValue;
        finalCycle.fees += fee;
        finalCycle.exitTime = fillTime;

        // Calculate duration
        const entryTime = finalCycle.entryTime || fillTime;
        const durationMs = fillTime.getTime() - entryTime.getTime();

        // Calculate average entry price
        const avgEntryPrice =
          finalCycle.entryQuantity > 0
            ? finalCycle.entryPriceSum / finalCycle.entryQuantity
            : price;

        const completedTrade: CompletedTrade = {
          id: fill.id,
          market,
          category: getTokenCategory(market),
          side: finalCycle.side,
          entryTime,
          exitTime: fillTime,
          durationMs: Math.max(0, durationMs),
          totalPnl: finalCycle.totalPnl,
          volume: finalCycle.volume,
          fees: finalCycle.fees,
          entryPrice: avgEntryPrice,
          exitPrice: price,
          netPnl: finalCycle.totalPnl - finalCycle.fees,
        };

        completedTrades.push(completedTrade);

        // Reset cycle for this market
        currentCycles.delete(market);

        // If flip, start new position
        if (fillType === "flip") {
          currentCycles.set(market, {
            market,
            side: positionSide,
            entryTime: fillTime,
            exitTime: null,
            fills: [],
            totalPnl: 0,
            volume: 0,
            fees: 0,
            entryPriceSum: 0,
            entryQuantity: 0,
          });
        }
      }
    }
  }

  return completedTrades;
}

/**
 * Hook that processes fills into completed trades with duration and category
 */
export function useCompletedTrades(fills: Fill[]): {
  trades: CompletedTrade[];
  isCalculating: boolean;
} {
  const trades = useMemo(() => {
    if (!fills.length) return [];
    return groupFillsIntoCompletedTrades(fills);
  }, [fills]);

  return {
    trades,
    isCalculating: false,
  };
}

import { type DateRangeValue, getDateRange } from "@/components/ui/date-range-selector";

/**
 * Filter trades by date range
 */
export function filterTradesByDateRange(
  trades: CompletedTrade[],
  dateRange: DateRangeValue
): CompletedTrade[] {
  if (dateRange === "all") return trades;

  const { from, to } = getDateRange(dateRange);

  return trades.filter((trade) => {
    const exitTime = trade.exitTime;
    return exitTime >= from && exitTime <= to;
  });
}

